import React from 'react';
import ActivityTimeline from './ActivityTimeline';
import { customerActivityService } from '@/api/services/customerActivity';
import type { CustomerActivityItem } from '@/api/services/customerActivity';
import { applyMarkRead } from '@/utils/customerActivityState';
import { invalidateCustomerActivityRefresh, subscribeCustomerActivityRefresh } from '@/utils/customerActivityRefresh';

type Props = {
  searchId: string;
  limit?: number;
};

export default function SearchActivityTab({ searchId, limit = 20 }: Props) {
  const [items, setItems] = React.useState<CustomerActivityItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  const loadItems = React.useCallback(async () => {
    const data = await customerActivityService.list({ search_id: searchId, page_size: limit });
    setItems(data.items);
  }, [searchId, limit]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadItems()
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadItems]);

  React.useEffect(() => subscribeCustomerActivityRefresh(() => {
    void loadItems();
  }), [loadItems]);

  return (
    <ActivityTimeline
      items={items}
      loading={loading}
      emptyTitle="No search activity yet"
      emptyDescription="Run this search to see results and notifications here."
      onMarkRead={(id) => {
        setItems((prev) => applyMarkRead(prev, id));
        void customerActivityService.markRead(id).then(() => {
          invalidateCustomerActivityRefresh();
        });
      }}
    />
  );
}
